// RouteAdvisor v2 — surfaces FAA Preferred IFR Routes (PIFR + TEC) AND
// FlightAware AeroAPI recently-cleared routes for the active plan's
// origin/destination pair, and splices a picked route directly into
// the route array.
//
// Two backing endpoints:
//   /api/route-advisor             — NFDC PIFR + TEC (free, daily blob)
//   /api/flightaware/recent-routes — AeroAPI Standard tier ($100/mo,
//                                    served only when the env var
//                                    FLIGHTAWARE_API_KEY is set)
//
// Picking a route runs each whitespace-separated token through the
// shared classifier in routeStringExpand.ts. Airway designators
// (V/J/T/Q + digits), procedure idents (e.g. WOONS2), and the
// origin/destination idents themselves are dropped. The remaining
// fix/navaid idents are batch-resolved via the search API and
// spliced into the route via planStore.replaceEnrouteSegment, which
// preserves the airport endpoints and any loaded SID/STAR/IAP legs.

import { useEffect, useMemo, useState, useRef } from 'react';
import { Compass, Loader2, Plane } from '../shell/Icon';
import { usePlanStore } from '../stores/planStore';
import type { PlanPoint, PlanPointKind } from '../stores/planStore';
import { searchAny, type SearchHit } from '../lib/search';
import { tokenizeAndClassify, resolvableTokens, findAirwayExpansions } from '../lib/routeStringExpand';
import { aeroapiGet, aeroapiSet } from '../lib/aeroapiCache';
import { useAircraftStore, selectActiveAircraft } from '../stores/aircraftStore';
import {
  classifyAircraftType,
  inferIcaoFromTypeLabel,
  routeMatchesClass,
  aircraftClassLabel,
  type AircraftClass,
} from '../lib/aircraftClass';

export interface PrefRoute {
  orig: string;
  dest: string;
  route: string;
  type: string;
  area: string;
  altitude: string;
  aircraft: string;
  dir: string;
  hours: string;
  seq: number;
  dArtcc: string;
  aArtcc: string;
}

interface RouteAdvisorResponse {
  origin: string;
  destination: string;
  fetchedAt: string;
  routes: PrefRoute[];
}

/** Shape from the /api/flightaware/recent-routes endpoint (Feature 2: /routes/{dest_id}). */
export interface FlightAwareRoute {
  /** Route string (waypoints / airways / procedures). */
  route: string;
  /** Number of times this route was filed in the lookback window. */
  count: number;
  /** Aircraft type codes that filed this route (e.g. ['C172', 'SR22']). */
  aircraftTypes: string[];
  /** Minimum filed altitude in hundreds of feet, or null. */
  filedAltitudeMin: number | null;
  /** Maximum filed altitude in hundreds of feet, or null. */
  filedAltitudeMax: number | null;
  /** Filed route distance string as returned by AeroAPI (e.g. "124 nm"), or null. */
  routeDistance: string | null;
  /** ISO timestamp of the most recent departure filed with this route. */
  lastDeparture: string | null;
}

interface FlightAwareResponse {
  origin: string;
  destination: string;
  fetchedAt: string;
  source: string;
  routes: FlightAwareRoute[];
}

function normIdent(s: string): string {
  const t = (s || '').trim().toUpperCase();
  if (t.length === 4 && t.startsWith('K')) return t.slice(1);
  return t;
}

function pifrTypeLabel(t: string): string {
  switch (t.toUpperCase()) {
    case 'L': return 'Low altitude';
    case 'H': return 'High altitude';
    case 'LSD': return 'Low single-direction';
    case 'HSD': return 'High single-direction';
    case 'SLD': return 'Single-line direction';
    case 'SHD': return 'Single-line high direction';
    case 'TEC': return 'Tower En-route';
    default: return t || '\u2014';
  }
}

function hitToPlanKind(h: SearchHit): PlanPointKind {
  if (h.kind === 'airport') return 'airport';
  if (h.kind === 'fix') return 'fix';
  return (h.badge || '').toUpperCase().includes('NDB') ? 'ndb' : 'vor';
}

// Fetch expanded intermediate fixes for an airway segment.
// Returns null on any error so callers can fall back to direct leg.
async function fetchAirwayExpansion(
  airwayIdent: string,
  fromIdent: string,
  toIdent: string,
): Promise<Array<{ ident: string; lat: number; lon: number }> | null> {
  try {
    const url =
      `/api/airway?ident=${encodeURIComponent(airwayIdent)}` +
      `&from=${encodeURIComponent(fromIdent)}&to=${encodeURIComponent(toIdent)}`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    const resp = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!resp.ok) {
      // 404 = fix not on airway; 503 = no NASR data yet.
      // Both are graceful-degradation cases — return null, fall back to direct.
      console.warn(`[RouteAdvisor] airway ${airwayIdent} expansion returned ${resp.status}`);
      return null;
    }
    const data = (await resp.json()) as { fixes: Array<{ ident: string; lat: number; lon: number }> };
    // The response includes endpoint fixes (from + to); we keep them as
    // the caller already has those PlanPoints — strip them to avoid duplication.
    const inner = data.fixes.slice(1, -1);
    return inner;
  } catch (e) {
    console.warn('[RouteAdvisor] airway expansion fetch failed:', e);
    return null;
  }
}

/** Resolve a route string into PlanPoints. Each whitespace-separated
 *  token is classified; airway tokens between two fix tokens are
 *  expanded by calling GET /api/airway?ident=X&from=A&to=B (falls back
 *  to direct leg on any error). Procedure / origin / destination tokens
 *  are dropped. The remaining fix-kind tokens are resolved via the
 *  search API; tokens that don't resolve are silently dropped. */
async function resolveRouteString(
  routeString: string,
  originIdent: string,
  destinationIdent: string,
): Promise<PlanPoint[]> {
  const classified = tokenizeAndClassify(routeString, originIdent, destinationIdent);

  // Step 1: identify airway expansion candidates.
  const expansions = findAirwayExpansions(classified);

  // Step 2: fetch all airway expansions in parallel.
  const expansionResults = await Promise.all(
    expansions.map((exp) =>
      fetchAirwayExpansion(exp.airwayIdent, exp.fromIdent, exp.toIdent),
    ),
  );
  // Build a map: airwayIdx → expanded intermediate PlanPoints
  const expansionMap = new Map<number, PlanPoint[]>();
  for (let i = 0; i < expansions.length; i++) {
    const fixes = expansionResults[i];
    if (fixes && fixes.length > 0) {
      expansionMap.set(expansions[i].airwayIdx, fixes.map((f) => ({
        ident: f.ident,
        lat: f.lat,
        lon: f.lon,
        kind: 'fix' as const,
        viaAirway: expansions[i].airwayIdent,
      })));
    }
  }

  // Step 3: resolve regular fix tokens via searchAny.
  const toResolve = resolvableTokens(classified);
  // Cap at 30 to avoid fanning out on weird inputs.
  const lookups = toResolve.slice(0, 30).map(async (t) => {
    try {
      const hits = await searchAny(t.ident, { limit: 5 });
      // Take the first hit whose ident matches exactly (after K-strip).
      const exact = hits.find((h) => normIdent(h.ident) === normIdent(t.ident));
      const chosen = exact ?? hits[0] ?? null;
      if (!chosen) return null;
      return {
        ident: chosen.ident,
        lat: chosen.lat,
        lon: chosen.lon,
        kind: hitToPlanKind(chosen),
      } as PlanPoint;
    } catch {
      return null;
    }
  });
  const resolvedFixes = await Promise.all(lookups);

  // Build a map: token ident → resolved PlanPoint (for inline splicing).
  const fixMap = new Map<string, PlanPoint>();
  for (let i = 0; i < toResolve.length; i++) {
    const p = resolvedFixes[i];
    if (p) fixMap.set(toResolve[i].ident, p);
  }

  // Step 4: reconstruct the route in token order, inserting expanded
  // airway fixes and resolved fix tokens.
  const result: PlanPoint[] = [];
  for (let i = 0; i < classified.length; i++) {
    const tok = classified[i];
    if (tok.kind === 'airway') {
      // If we have an expansion for this index, insert intermediate fixes.
      const expanded = expansionMap.get(i);
      if (expanded) {
        for (const p of expanded) result.push(p);
      }
      // If no expansion, the airway is silently dropped — direct leg
      // between the flanking fixes (v2 fallback behaviour).
    } else if (tok.kind === 'fix') {
      const p = fixMap.get(tok.ident);
      if (p) result.push(p);
    }
    // origin / destination / procedure / unknown tokens are dropped.
  }
  return result;
}

export default function RouteAdvisor() {
  const route = usePlanStore((s) => s.route);
  const replaceEnrouteSegment = usePlanStore((s) => s.replaceEnrouteSegment);

  // Identify origin + destination airports.
  const origin = useMemo(() => {
    const found = route.find((p) => p.kind === 'airport');
    return found ?? null;
  }, [route]);
  const destination = useMemo(() => {
    for (let i = route.length - 1; i >= 0; i--) {
      if (route[i].kind === 'airport') return route[i];
    }
    return null;
  }, [route]);

  const [pifrState, setPifrState] = useState<{
    loading: boolean;
    error: string | null;
    response: RouteAdvisorResponse | null;
  }>({ loading: false, error: null, response: null });

  const [faState, setFaState] = useState<{
    loading: boolean;
    error: string | null;
    response: FlightAwareResponse | null;
    available: boolean; // false when the server returns 503 (no API key)
    cachedAt: number | null; // Date.now() of the last known-good response
    fromCache: boolean; // true when currently showing cached data, network not yet back
  }>({ loading: false, error: null, response: null, available: true, cachedAt: null, fromCache: false });

  // Aircraft class filter — derive from the active aircraft's icaoType/typeLabel.
  const activeAircraft = useAircraftStore(selectActiveAircraft);
  const userClass: AircraftClass = (() => {
    const icao = activeAircraft?.icaoType ?? inferIcaoFromTypeLabel(activeAircraft?.typeLabel ?? null);
    return classifyAircraftType(icao);
  })();

  const [applyingRoute, setApplyingRoute] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Opt-in gate (2026-05-18): the advisor used to fetch on every plan
  // change, which burned the FlightAware Personal-tier quota and surfaced
  // routes the user didn't ask for. Now the user clicks "Show suggested
  // routes" to load. The button reappears whenever the airport pair
  // changes so the panel never shows stale routes for a different pair.
  const [loaded, setLoaded] = useState(false);
  const pairKey = `${origin?.ident ?? ''}|${destination?.ident ?? ''}`;
  const isFirstRender = useRef(true);
  useEffect(() => {
    // Skip the very first render so we don't double-reset on mount.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // Reset whenever the airport pair changes so the button reappears.
    setLoaded(false);
    setPifrState({ loading: false, error: null, response: null });
    setFaState({ loading: false, error: null, response: null, available: true, cachedAt: null, fromCache: false });
  }, [pairKey]);

  // Fetch both sources only after the user opts in.
  useEffect(() => {
    if (!loaded) return;
    if (!origin || !destination || origin === destination) {
      setPifrState({ loading: false, error: null, response: null });
      setFaState({ loading: false, error: null, response: null, available: true, cachedAt: null, fromCache: false });
      return;
    }
    const o = normIdent(origin.ident);
    const d = normIdent(destination.ident);
    if (!o || !d) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    setPifrState((s) => ({ ...s, loading: true, error: null }));
    fetch(`/api/route-advisor?origin=${encodeURIComponent(o)}&destination=${encodeURIComponent(d)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<RouteAdvisorResponse>;
      })
      .then((response) => {
        if (cancelled) return;
        setPifrState({ loading: false, error: null, response });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPifrState({
          loading: false,
          error: (e as Error)?.message ?? String(e),
          response: null,
        });
      });

    // ── FlightAware: optimistic UI from IDB cache, then background network refresh ──

    const cacheKey = `routes|${o}|${d}`;

    // Step 1: render from IDB cache immediately so the panel never starts blank.
    aeroapiGet<FlightAwareResponse>(cacheKey).then((entry) => {
      if (cancelled) return;
      if (entry) {
        setFaState((s) => ({
          ...s,
          loading: true, // still waiting on network
          response: entry.data,
          available: true,
          cachedAt: entry.cachedAt,
          fromCache: true,
        }));
      } else {
        setFaState((s) => ({ ...s, loading: true, error: null, fromCache: false }));
      }
    });

    // Step 2: fast-path offline check — skip network entirely when offline.
    if (!navigator.onLine) {
      setFaState((s) => ({ ...s, loading: false }));
      return;
    }

    // Step 3: background network request with 5 s AbortController ceiling.
    controller = new AbortController();
    timer = setTimeout(() => controller!.abort(), 5000);

    fetch(
      `/api/flightaware/recent-routes?origin=${encodeURIComponent(o)}&destination=${encodeURIComponent(d)}`,
      { signal: controller.signal },
    )
      .then(async (r) => {
        if (timer) clearTimeout(timer);
        if (r.status === 503) {
          // Server signals that FlightAware isn't configured. Hide the
          // section silently — boss already knows whether he wired up
          // an API key.
          return { available: false as const };
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as FlightAwareResponse;
        return { available: true as const, body };
      })
      .then((result) => {
        if (cancelled) return;
        if (!result.available) {
          setFaState({ loading: false, error: null, response: null, available: false, cachedAt: null, fromCache: false });
        } else {
          // Write to IDB then update state with fresh data.
          aeroapiSet(cacheKey, result.body);
          setFaState({ loading: false, error: null, response: result.body, available: true, cachedAt: Date.now(), fromCache: false });
        }
      })
      .catch((e: unknown) => {
        if (timer) clearTimeout(timer);
        if (cancelled) return;
        // Network failed or timed out — keep any cached render, just stop loading.
        setFaState((s) => ({
          ...s,
          loading: false,
          // Only surface an error if we have no cached data to show.
          error: s.response ? null : ((e as Error)?.message ?? String(e)),
          fromCache: s.response != null,
        }));
      });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [origin, destination, loaded]);

  async function applyRoute(routeString: string): Promise<void> {
    if (!origin || !destination) return;
    setApplyError(null);
    setApplyingRoute(routeString);
    try {
      const points = await resolveRouteString(routeString, origin.ident, destination.ident);
      // Splice into the active route. The store preserves origin /
      // destination airports + any loaded procedures.
      replaceEnrouteSegment(points);
      if (points.length === 0) {
        setApplyError('No tokens in this route resolved to known fixes. Add waypoints manually.');
      }
    } catch (e: unknown) {
      setApplyError((e as Error)?.message ?? String(e));
    } finally {
      setApplyingRoute(null);
    }
  }

  if (!origin || !destination || origin === destination) {
    return (
      <div className="text-[11px] text-cockpit-400 italic px-1 py-1.5">
        Add a departure and destination airport to see preferred IFR routes.
      </div>
    );
  }

  // Opt-in: don't fetch until the user clicks. Button reappears whenever
  // the airport pair changes (see pairKey effect above).
  if (!loaded) {
    return (
      <div className="px-1 py-1.5">
        <button
          onClick={() => setLoaded(true)}
          className="w-full text-xs bg-cockpit-700/40 hover:bg-cockpit-700/60 border border-cockpit-600 text-cockpit-100 rounded-md px-3 py-2 transition-colors"
        >
          Show suggested routes for {origin.ident} → {destination.ident}
        </button>
        <div className="mt-1.5 text-[10px] text-cockpit-400 italic">
          FAA preferred IFR (NFDC) + recently-cleared routes (FlightAware). Loads on demand.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {applyError && (
        <div className="text-[11px] text-amber-400 px-1">
          {applyError}
        </div>
      )}

      {/* ── FAA NFDC ── */}
      <RouteSection
        title="FAA preferred (NFDC)"
        loading={pifrState.loading}
        error={pifrState.error}
        emptyMessage={`No FAA preferred IFR routes published for ${origin.ident} \u2192 ${destination.ident}. File direct or via airways. Most GA city pairs don\u2019t have a PIFR.`}
        rows={(pifrState.response?.routes ?? []).map((r, i) => ({
          key: `pifr-${r.seq}-${i}`,
          subtitle: pifrTypeLabel(r.type),
          tag: r.dArtcc + (r.aArtcc && r.aArtcc !== r.dArtcc ? ` \u2192 ${r.aArtcc}` : ''),
          route: r.route,
          meta: [
            r.altitude && `ALT: ${r.altitude}`,
            r.aircraft && `A/C: ${r.aircraft}`,
            r.dir && `DIR: ${r.dir}`,
            r.hours && `HRS: ${r.hours}`,
          ].filter(Boolean) as string[],
        }))}
        footer={pifrState.response?.fetchedAt && `NFDC data refreshed ${new Date(pifrState.response.fetchedAt).toLocaleDateString()}`}
        applyingRoute={applyingRoute}
        onApply={applyRoute}
      />

      {/* ── FlightAware recent clearances (when configured) ── */}
      {faState.available && (() => {
        const allRoutes = faState.response?.routes ?? [];
        const filteredRoutes = allRoutes.filter((r) =>
          routeMatchesClass(r.aircraftTypes, userClass),
        );
        const filterLabel = userClass !== 'unknown'
          ? `Filtered for ${aircraftClassLabel(userClass)} \u2014 ${filteredRoutes.length} of ${allRoutes.length} routes`
          : null;
        return (
        <RouteSection
          title="Recently cleared (FlightAware)"
          loading={faState.loading && !faState.response}
          error={faState.error}
          emptyMessage={`No recent IFR clearances on file for ${origin.ident} \u2192 ${destination.ident}.`}
          filterLabel={filterLabel}
          rows={filteredRoutes.map((r, i) => {
            // For the tag, show only the types that match the user's class.
            const matchingTypes = userClass === 'unknown'
              ? r.aircraftTypes
              : r.aircraftTypes.filter((t) => {
                  const cls = classifyAircraftType(t);
                  // show the type if it classifies to user's class OR unknown (no-info)
                  return cls === 'unknown' || routeMatchesClass([t], userClass);
                });
            const displayTypes = matchingTypes.length > 0 ? matchingTypes : r.aircraftTypes;
            const tag = displayTypes.length > 0
              ? displayTypes.slice(0, 3).join(', ') + (displayTypes.length > 3 ? ` +${displayTypes.length - 3} more` : '')
              : '';
            return ({
              key: `fa-${i}`,
              subtitle: r.count > 0 ? `Filed ${r.count}x` : 'Filed',
              tag,
              route: r.route,
              meta: [
                (() => {
                  if (r.filedAltitudeMin == null) return false;
                  if (r.filedAltitudeMax != null && r.filedAltitudeMax !== r.filedAltitudeMin) {
                    return `FILED: ${r.filedAltitudeMin}00\u2013${r.filedAltitudeMax}00 ft`;
                  }
                  return `FILED: ${r.filedAltitudeMin}00 ft`;
                })(),
                r.routeDistance ? `DIST: ${r.routeDistance}` : false,
                (() => {
                  if (!r.lastDeparture) return false;
                  const d = new Date(r.lastDeparture);
                  const ageDays = (Date.now() - d.getTime()) / 86_400_000;
                  if (ageDays < 7) {
                    const rounded = Math.round(ageDays);
                    return `LAST: ${rounded <= 0 ? 'today' : rounded === 1 ? '1 day ago' : `${rounded} days ago`}`;
                  }
                  return `LAST: ${d.toLocaleDateString()}`;
                })(),
              ].filter(Boolean) as string[],
              icon: <Plane size={11} />,
            });
          })}
          footer={
            faState.cachedAt
              ? `FlightAware \u2014 last refreshed ${new Date(faState.cachedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
              : faState.response?.source
          }
          applyingRoute={applyingRoute}
          onApply={applyRoute}
        />
        );
      })()}
    </div>
  );
}

interface RouteRow {
  key: string;
  subtitle: string;
  tag: string;
  route: string;
  meta: string[];
  icon?: React.ReactNode;
}

interface RouteSectionProps {
  title: string;
  loading: boolean;
  error: string | null;
  emptyMessage: string;
  /** Optional subtitle shown below the title when a filter is active. */
  filterLabel?: string | null;
  rows: RouteRow[];
  footer: string | false | null | undefined;
  applyingRoute: string | null;
  onApply: (route: string) => void;
}

function RouteSection(props: RouteSectionProps): React.ReactElement {
  return (
    <div>
      <div className="text-[10px] text-cockpit-400 uppercase tracking-wider mb-0.5">
        {props.title}
      </div>
      {props.filterLabel && (
        <div className="text-[9px] text-fuchsia-400/70 mb-1 px-0.5">
          {props.filterLabel}
        </div>
      )}
      {props.loading ? (
        <div className="flex items-center gap-2 text-[11px] text-cockpit-300 px-1 py-1.5">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>{"Looking up routes…"}</span>
        </div>
      ) : props.error ? (
        <div className="text-[11px] text-amber-400 px-1 py-1.5">
          Unavailable: {props.error}
        </div>
      ) : props.rows.length === 0 ? (
        <div className="text-[11px] text-cockpit-400 px-1 py-1.5">
          {props.emptyMessage}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {props.rows.map((r) => (
            <li
              key={r.key}
              className="rounded-md border border-cockpit-700 bg-cockpit-800/40 p-2 space-y-1"
            >
              <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider">
                <span className="flex items-center gap-1 text-cockpit-300">
                  {r.icon}
                  {r.subtitle}
                </span>
                <span className="text-cockpit-400 font-mono">{r.tag}</span>
              </div>
              <div className="font-mono text-[12px] text-cockpit-100 break-all">
                {r.route}
              </div>
              {r.meta.length > 0 && (
                <div className="text-[10px] text-cockpit-300 space-x-2">
                  {r.meta.map((m, i) => <span key={i}>{m}</span>)}
                </div>
              )}
              <button
                type="button"
                onClick={() => props.onApply(r.route)}
                disabled={props.applyingRoute != null}
                className="inline-flex items-center gap-1.5 mt-1 rounded-md border border-fuchsia-400/40 bg-fuchsia-400/10 px-2 py-1 text-[11px] font-semibold text-fuchsia-200 hover:bg-fuchsia-400/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {props.applyingRoute === r.route ? (
                  <>
                    <Loader2 size={11} className="animate-spin" />
                    {"Applying…"}
                  </>
                ) : (
                  <>
                    <Compass size={11} />
                    Apply this route
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {props.footer && (
        <div className="text-[9px] text-cockpit-500 mt-1">
          {props.footer}
        </div>
      )}
    </div>
  );
}
